import type { ComponentType } from "react";
import { useCallback } from "react";

import { UpgradeAnchorCell, type UpgradeAnchorCellProps } from "@similarweb/pro-logic-components";

import { swSettings } from "app/common/services/swSettings";
import { type HookType, openHooksModalV5 } from "app/services/ModalService";
import { goToPackagesPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToPackagesPage";
import { isTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import LocationService from "pro-features/components/Modals/src/UnlockModal/LocationService";
import { useTrack } from "pro-features/components/WithTrack/src/useTrack";

export type UpgradeCellHookParams = {
    featureKey?: string;
    location?: HookType;
    requestOptions?: {
        isRedirectToNT: boolean;
        packageUrl?: string;
    };
};

type UpgradeViewProps = Pick<UpgradeAnchorCellProps, "children" | "onClick"> & {
    packageUrl?: string;
};

type HookValue = {
    triggerUpgradeAction: () => void;
    renderUpgradeView: ComponentType<UpgradeViewProps>;
};

const DEFAULT_FEATURE_KEY = "GetMoreResults";

const NoTouchUpgradeCellView = ({ packageUrl, ...otherProps }: UpgradeViewProps) => (
    <UpgradeAnchorCell
        {...otherProps}
        href={packageUrl}
    />
);

export const useUpgradeCellHook = ({ featureKey, location, requestOptions }: UpgradeCellHookParams = {}): HookValue => {
    const [track] = useTrack();

    // Safely check if redirect to no-touch is required
    const shouldRedirectToNoTouchSubscriptions = requestOptions?.isRedirectToNT || false;

    const triggerModal = useCallback((): void => {
        openHooksModalV5(featureKey ?? DEFAULT_FEATURE_KEY, location);
    }, [featureKey, location]);

    const triggerNoTouch = useCallback((): void => {
        track("Internal Link", "click", `UpgradeLink/${LocationService.getCurrentLocationTracking()}`);
    }, [track]);

    // Handle no-touch redirect
    if (shouldRedirectToNoTouchSubscriptions) {
        return {
            triggerUpgradeAction: triggerNoTouch,
            renderUpgradeView: NoTouchUpgradeCellView,
        };
    }

    // Handle trial users with requestOptions
    if (isTrialOrAfterTrial(swSettings)) {
        if (requestOptions?.packageUrl) {
            return {
                triggerUpgradeAction: () => window.open(requestOptions.packageUrl, "_blank"),
                renderUpgradeView: UpgradeAnchorCell,
            };
        }

        return {
            triggerUpgradeAction: () => goToPackagesPage(),
            renderUpgradeView: UpgradeAnchorCell,
        };
    }

    // Default: trigger modal
    return {
        triggerUpgradeAction: triggerModal,
        renderUpgradeView: UpgradeAnchorCell,
    };
};
